/**
 * W-011 — Subject form (create + edit). Stories: US-A02.
 *
 * AC1 bilingual name and description, cover image, streams
 * AC2 saved as a draft, invisible in the app
 * AC3 publish makes it visible to students in the assigned streams
 * AC6 publishing is blocked when a required French field is empty
 */
import { Create, Edit, useForm } from '@refinedev/antd'
import { useInvalidate } from '@refinedev/core'
import type { HttpError } from '@refinedev/core'
import { Alert, Card, Form, Input, Select, Switch, Typography } from 'antd'
import type { FormInstance } from 'antd'
import { useMemo, useState } from 'react'
import { BilingualField } from '@/components'
import { recordAudit } from '@/lib/audit'
import { useAdminTranslate } from '@/providers/i18n-provider'
import { checkBilingualForPublish } from '@/lib/bilingual'
import { supabaseClient } from '@/lib/supabase'
import type { Subject } from '@/types'
import { useStreams } from './useStreams'

/**
 * The form carries one field the `subjects` row does not: `stream_ids`, which is
 * written to the `subject_streams` join table separately. It is optional so that
 * the row handed to `onFinish` — the same values minus `stream_ids` — is a valid
 * value of this type.
 */
interface SubjectFormValues extends Omit<Subject, 'id' | 'created_at' | 'updated_at'> {
  stream_ids?: string[]
}

/** Shape returned by the edit query, which joins the streams in (`meta.select`). */
interface SubjectWithStreams extends Subject {
  subject_streams?: { stream_id: string }[]
}

/**
 * `subject_streams` is a join table, so the form writes it separately from the
 * row itself (docs/03 §4.2). Done after the subject insert/update returns, in
 * the same user action.
 */
async function syncStreams(subjectId: string, streamIds: string[]): Promise<void> {
  const { error: deleteError } = await supabaseClient
    .from('subject_streams')
    .delete()
    .eq('subject_id', subjectId)
  if (deleteError) throw deleteError

  if (streamIds.length === 0) return
  const { error } = await supabaseClient
    .from('subject_streams')
    .insert(streamIds.map(streamId => ({ subject_id: subjectId, stream_id: streamId })))
  if (error) throw error
}

const SubjectFormFields = ({
  publishBlocked,
  onPublishToggle,
}: {
  publishBlocked: string | null
  onPublishToggle: (next: boolean) => void
}) => {
  const translate = useAdminTranslate()
  const { streams, isLoading } = useStreams()

  return (
    <>
      <Card
        size="small"
        title={translate('admin.screens.subjectForm.identity')}
        style={{ marginBottom: 16 }}
      >
        <BilingualField
          name="name"
          label={translate('admin.columns.name')}
          requiredAr
          maxLength={120}
        />
        <BilingualField
          name="description"
          label={translate('admin.common.description')}
          multiline
          maxLength={1000}
        />
      </Card>

      <Card
        size="small"
        title={translate('admin.screens.subjectForm.distribution')}
        style={{ marginBottom: 16 }}
      >
        <Form.Item
          name="stream_ids"
          label={translate('admin.screens.subjectForm.streamsLabel')}
          rules={[
            { required: true, message: translate('admin.screens.subjectForm.streamsRequired') },
          ]}
          help={translate('admin.screens.subjectForm.streamsHelp')}
        >
          <Select
            mode="multiple"
            loading={isLoading}
            optionFilterProp="label"
            options={streams.map(stream => ({
              value: stream.id,
              label: `${stream.name_fr} — ${stream.name_ar}`,
            }))}
          />
        </Form.Item>

        <Form.Item name="cover_url" label={translate('admin.screens.subjectForm.coverLabel')}>
          {/* TODO(US-A02): the cover goes to the same Bunny Storage zone as the
              PDFs (docs/03 §5.3). Until the storage edge function is named by
              the backend agent, the form accepts a URL. */}
          <Input placeholder="https://…" />
        </Form.Item>

        {publishBlocked ? (
          <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={publishBlocked} />
        ) : null}

        <Form.Item
          name="is_published"
          label={translate('admin.screens.subjectForm.publishedLabel')}
          valuePropName="checked"
          help={translate('admin.screens.subjectForm.publishedHelp')}
        >
          <Switch onChange={onPublishToggle} />
        </Form.Item>
      </Card>
    </>
  )
}

function usePublishGuard(form: FormInstance<SubjectFormValues>) {
  const translate = useAdminTranslate()
  const [publishBlocked, setPublishBlocked] = useState<string | null>(null)

  const onPublishToggle = (next: boolean) => {
    if (!next) {
      setPublishBlocked(null)
      return
    }
    const result = checkBilingualForPublish(form.getFieldsValue(true), [
      { field: 'name', label: translate('admin.publishGuard.fieldName') },
      { field: 'description', label: translate('admin.publishGuard.fieldDescription') },
    ])
    if (!result.ok) {
      // AC6 — blocked, with a message that names the missing fields.
      form.setFieldValue('is_published', false)
      setPublishBlocked(result.message ?? null)
    } else {
      setPublishBlocked(null)
    }
  }

  return { publishBlocked, onPublishToggle, setPublishBlocked }
}

export const SubjectCreatePage = () => {
  const translate = useAdminTranslate()
  const { formProps, saveButtonProps, form, onFinish } = useForm<
    Subject,
    HttpError,
    SubjectFormValues
  >({
    resource: 'subjects',
    action: 'create',
    redirect: 'edit',
  })
  const { publishBlocked, onPublishToggle } = usePublishGuard(form)
  const invalidate = useInvalidate()

  const handleFinish = async (values: SubjectFormValues) => {
    const { stream_ids: streamIds, ...row } = values
    const created = (await onFinish({ ...row, sort_order: row.sort_order ?? 0 })) as
      { data?: Subject } | undefined
    const id = created?.data?.id
    if (id) {
      await syncStreams(id, streamIds ?? [])
      await invalidate({ resource: 'subjects', invalidates: ['list'] })
    }
  }

  return (
    <Create saveButtonProps={saveButtonProps} title={translate('admin.pages.newSubject')}>
      <Form {...formProps} layout="vertical" onFinish={handleFinish}>
        <Typography.Paragraph type="secondary">
          {translate('admin.screens.subjectForm.createHint')}
        </Typography.Paragraph>
        <SubjectFormFields publishBlocked={publishBlocked} onPublishToggle={onPublishToggle} />
      </Form>
    </Create>
  )
}

export const SubjectEditPage = () => {
  const translate = useAdminTranslate()
  const { formProps, saveButtonProps, form, onFinish, query } = useForm<
    SubjectWithStreams,
    HttpError,
    SubjectFormValues
  >({
    resource: 'subjects',
    action: 'edit',
    meta: { select: '*, subject_streams(stream_id)' },
  })
  const { publishBlocked, onPublishToggle } = usePublishGuard(form)

  const previous = query?.data?.data

  // The join rows are flattened into the multi-select's value here rather than
  // in a react-query `select`, which would have to restate refine's query key.
  const initialValues = useMemo<SubjectFormValues | undefined>(
    () =>
      previous
        ? {
            ...previous,
            stream_ids: (previous.subject_streams ?? []).map(row => row.stream_id),
          }
        : undefined,
    [previous],
  )

  const handleFinish = async (values: SubjectFormValues) => {
    const { stream_ids: streamIds, ...row } = values
    await onFinish(row)
    if (previous?.id) {
      await syncStreams(previous.id, streamIds ?? [])
      if (previous.is_published !== row.is_published) {
        await recordAudit({
          action: row.is_published ? 'subject.publish' : 'subject.unpublish',
          target: 'subjects',
          targetId: previous.id,
          before: { is_published: previous.is_published },
          after: { is_published: row.is_published },
        })
      }
    }
  }

  return (
    <Edit saveButtonProps={saveButtonProps} title={translate('admin.pages.editSubject')}>
      <Form {...formProps} initialValues={initialValues} layout="vertical" onFinish={handleFinish}>
        <SubjectFormFields publishBlocked={publishBlocked} onPublishToggle={onPublishToggle} />
      </Form>
    </Edit>
  )
}
